import { useId, useRef, useState } from 'react';
import { endpoints } from '@/api/endpoints';
import { useLanguage } from '@/i18n/LanguageContext';
import { statusLabel } from '@/components/StatusBadge';
import { SkuMeta } from '@/components/SkuMeta';
import { STOCK_REASON_CODES, type OrderShortfall, type ShortfallAction, type StockReasonCode } from '@/api/types';
import { ActionError, describeActionError, fromScaled, newOpId, plainQty, stockReasonLabel, toScaled } from './shared';

const REASONS = STOCK_REASON_CODES.filter((r) => r !== 'FOUND');
const TITLE_KEY = { REALLOCATE: 'exc.title.REALLOCATE', REDUCE: 'exc.title.REDUCE', CANCEL_LINE: 'exc.title.CANCEL_LINE' } as const;
const CONFIRM_KEY = { REALLOCATE: 'exc.confirm.REALLOCATE', REDUCE: 'exc.confirm.REDUCE', CANCEL_LINE: 'exc.confirm.CANCEL_LINE' } as const;

/** Reason -> confirm (restating the consequence) -> result, for one short order line. */
export function ResolveShortfallDialog({
  line, action, onClose, onDone,
}: { line: OrderShortfall; action: ShortfallAction; onClose: () => void; onDone: () => void }): JSX.Element {
  const { t } = useLanguage();
  const uid = useId();
  const [step, setStep] = useState<'form' | 'confirm' | 'done'>('form');
  const [reason, setReason] = useState<StockReasonCode | ''>('');
  const [note, setNote] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string[]>([]);
  const opId = useRef(newOpId());
  const touch = (): void => {
    opId.current = newOpId();
  };

  const missing = plainQty(line.shortfall);
  const keep = fromScaled(toScaled(line.qty_ordered) - toScaled(line.shortfall));
  const intro =
    action === 'REALLOCATE'
      ? t('exc.intro.REALLOCATE', { qty: missing })
      : action === 'REDUCE'
        ? t('exc.intro.REDUCE', { keep, qty: missing })
        : t('exc.intro.CANCEL_LINE');
  const title = t(TITLE_KEY[action]);

  function onContinue(): void {
    if (!reason) return setFormError(t('stx.reasonMissing'));
    if (reason === 'OTHER' && note.trim().length < 3) return setFormError(t('stx.noteMissing'));
    setFormError(null);
    setStep('confirm');
  }

  async function submit(): Promise<void> {
    if (busy || !reason) return;
    setBusy(true);
    setSubmitError(null);
    try {
      const res = await endpoints.resolveShortfall({
        opId: opId.current, lineId: line.line_id, action, reasonCode: reason, ...(note.trim() ? { note: note.trim() } : {}),
      });
      const state = statusLabel(t, res.orderState);
      const lines: string[] = [];
      if (action === 'REALLOCATE') {
        lines.push(res.newTasks.length > 0 ? t('exc.done.REALLOCATE', { n: res.newTasks.length }) : t('exc.done.REALLOCATE_NONE'));
        if (toScaled(res.shortfall) > BigInt(0)) lines.push(t('exc.done.stillMissing', { n: plainQty(res.shortfall) }));
      } else if (action === 'REDUCE') {
        lines.push(t('exc.done.REDUCE', { state }));
      } else {
        lines.push(t('exc.done.CANCEL_LINE', { state }));
      }
      setResult(lines);
      setStep('done');
    } catch (err) {
      setSubmitError(describeActionError(t, err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card stock-panel" role="dialog" aria-label={title}>
      <h2 className="card-title">{title}</h2>
      <dl className="kv">
        <dt>{t('exc.colOrder')}</dt>
        <dd>{line.number}</dd>
        <dt>{t('exc.colProduct')}</dt>
        <dd>
          {line.sku_code} — {line.sku_name}
          <SkuMeta code={line.sku_code} article={line.article} size={line.size} color={line.color} barcodes={line.barcodes} />
        </dd>
        <dt>{t('exc.colMissing')}</dt>
        <dd>{missing}</dd>
      </dl>
      <p>{intro}</p>

      {step === 'form' && (
        <form
          className="order-create"
          onSubmit={(e) => {
            e.preventDefault();
            onContinue();
          }}
        >
          <div className="field-row">
            <label htmlFor={`${uid}-reason`}>{t('stx.reason')}</label>
            <select
              id={`${uid}-reason`}
              value={reason}
              onChange={(e) => {
                setReason(e.target.value as StockReasonCode | '');
                touch();
              }}
            >
              <option value="">{t('stx.reasonPlaceholder')}</option>
              {REASONS.map((r) => (
                <option key={r} value={r}>
                  {stockReasonLabel(t, r)}
                </option>
              ))}
            </select>
          </div>
          <div className="field-row">
            <label htmlFor={`${uid}-note`}>{reason === 'OTHER' ? t('stx.noteRequired') : t('stx.note')}</label>
            <textarea
              id={`${uid}-note`}
              rows={2}
              value={note}
              onChange={(e) => {
                setNote(e.target.value);
                touch();
              }}
            />
          </div>
          {formError && (
            <div className="field-error" role="alert">
              {formError}
            </div>
          )}
          <div className="form-actions">
            <button type="submit" className="primary">
              {t('stx.continue')}
            </button>
            <button type="button" onClick={onClose}>
              {t('stx.cancel')}
            </button>
          </div>
        </form>
      )}

      {step === 'confirm' && reason && (
        <div>
          <div className={`card ${action === 'CANCEL_LINE' ? 'danger-card' : 'confirm-card'}`}>
            <p style={{ margin: 0, fontWeight: 600 }}>{intro}</p>
            <p className="field-hint">{t('stx.reasonLine', { reason: stockReasonLabel(t, reason) })}</p>
            {note.trim() && <p className="field-hint">{t('stx.noteLine', { note: note.trim() })}</p>}
          </div>
          {submitError && <ActionError message={submitError} />}
          <div className="form-actions">
            <button type="button" className={action === 'CANCEL_LINE' ? 'danger' : 'primary'} onClick={() => void submit()} disabled={busy}>
              {busy ? t('stx.working') : t(CONFIRM_KEY[action])}
            </button>
            <button type="button" onClick={() => setStep('form')} disabled={busy}>
              {t('stx.back')}
            </button>
            {submitError && (
              <button type="button" onClick={onClose}>
                {t('stx.close')}
              </button>
            )}
          </div>
        </div>
      )}

      {step === 'done' && (
        <div className="success-card card" role="status">
          {result.map((r) => (
            <p key={r} style={{ margin: 0, fontWeight: 600 }}>
              {r}
            </p>
          ))}
          <div className="form-actions">
            <button type="button" className="primary" onClick={onDone}>
              {t('stx.close')}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
